import { Add, CheckCircle, DeleteOutline, Edit, ExpandLess, KeyboardArrowDown } from "@mui/icons-material";
import { Box, Button, Chip, Collapse, Divider, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, ListSubheader, MenuItem, Paper, Select, Switch, TextField, Typography, useMediaQuery } from "@mui/material";
import { menuPropsModal } from "../../../../../utils";
import { colors, alphaVar } from "../../../../ui/colors";
import { useQuoteServicesQuery } from "../hooks/useQuoteServicesQuery";
import type { BasicRow } from "../types/basicQuote.types";
import { useState } from "react";
import { ServiceAliasField } from "@components/common/ServiceAliasField";
import { EFrequencyType, JOB_FREQUENCY_OPTIONS, getJobFrequencyLabel } from "../../../../scope-of-work/helpers";

const MEASURE_OPTIONS = [
  { group: "Area", value: "sqft", label: "Square Feet" },
  { group: "Area", value: "sqm", label: "Square Meters" },
  { group: "Time", value: "minutes", label: "Minute(s)" },
  { group: "Time", value: "hours", label: "Hour(s)" },
  { group: "Time", value: "days", label: "Day(s)" },
  { group: "Time", value: "weeks", label: "Week(s)" },
  { group: "Time", value: "months", label: "Month(s)" },
  // { group: "Time", value: "years", label: "Year(s)" },
];

const groupOptions = (options: { group?: string; value: string; label: string }[]) => {
  const grouped: Record<string, { group: string; options: { value: string; label: string }[] }> = {};

  options.forEach((option) => {
    const groupName = option.group || "Other";
    if (!grouped[groupName]) {
      grouped[groupName] = { group: groupName, options: [] };
    }
    grouped[groupName].options.push({ value: option.value, label: option.label });
  });

  return Object.values(grouped);
};

const INPUT_HEIGHT = 40;

const cardShellSx = {
  borderRadius: "18px",
  overflow: "hidden",
  bgcolor: "transparent",
};

const cardHeaderSx = {
  px: { xs: 2, sm: 2.5 },
  py: 1.75,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
};

const formatMoney = (value: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

const getMeasureLabel = (measure?: string) => MEASURE_OPTIONS.find((option) => option.value === measure)?.label ?? measure ?? "units";

const baseInputStyles = {
  "& .MuiInputLabel-root": {
    color: colors.gray[400],
    fontSize: "14px",
    fontWeight: 700,
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    padding: "0 4px",
    zIndex: 1,
    transform: "translate(12px, -2px) scale(0.85)",
    transition: "all 0.2s ease-out",
    pointerEvents: "none",

    "&.Mui-focused, &.MuiFormLabel-filled, &.Mui-error": {
      color: colors.orange.base,
      transform: "translate(12px, -2px) scale(0.85)",
    },
  },

  "& .MuiInputLabel-root.Mui-error": {
    color: colors.gray[400],
  },

  "&.Mui-error fieldset": {
    borderColor: `${colors.danger[500]} !important`,
  },

  "& .MuiOutlinedInput-root": {
    height: INPUT_HEIGHT,
    borderRadius: "12px",
    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
    marginTop: "10px",
    transition: "all 0.2s ease-in-out",

    "& fieldset": {
      border: `1px solid ${colors.gray[200]}`,
      transition: "border-color 0.2s ease-in-out",
      "& legend": {
        display: "none",
      },
    },

    "&:hover fieldset": {
      borderColor: colors.gray[300],
    },

    "&.Mui-focused": {
      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      "& fieldset": {
        border: `2px solid ${colors.orange.base}`,
      },
    },
  },
};
export const textFieldStyles = { ...baseInputStyles };

const multilineStyles = {
  ...baseInputStyles,
  "& .MuiOutlinedInput-root": {
    ...baseInputStyles["& .MuiOutlinedInput-root"],
    height: "auto",
    minHeight: 80,
    alignItems: "flex-start",
    padding: "12px 16px",
  },
};

type Props = {
  row: BasicRow;
  index: number;
  rowsCount: number;
  onDeleteLocalAndRedux: (index: number) => void;
  onUpdateField: (index: number, field: keyof BasicRow, value: any) => void;
  onSaveAndLock: (index: number) => void;
  onEnterEditMode: (index: number) => void;
  onRemoveFromRedux: (rowId: string) => void;
  showHeader?: boolean;
  onlyOneService?: boolean;
  canDelete?: boolean;
};

export const QuoteBasicRow = ({
  row,
  index,
  onDeleteLocalAndRedux,
  onUpdateField,
  onSaveAndLock,
  onEnterEditMode,
  onRemoveFromRedux,
  showHeader,
  onlyOneService = false,
  canDelete,
}: Props) => {
  const isLocked = row.mode === "locked";
  const { data, isLoading } = useQuoteServicesQuery();
  const services = data?.items || [];
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);

  const frequencyTypeValue = row.frequencyType || EFrequencyType.WEEKLY;
  const hasValidServiceNameOverride = !row.useServiceNameOverride || Boolean(row.serviceNameOverride?.trim());

  const isFormValid =
    Boolean(row.service) &&
    Boolean(row.measure) &&
    Boolean(frequencyTypeValue) &&
    Number(row.price) > 0 &&
    Number(row.units) > 0 &&
    hasValidServiceNameOverride;

  const handleSave = () => {
    setWasSubmitted(true);
    if (isFormValid) {
      onSaveAndLock(index);
    }
  };

  const isXs = useMediaQuery("(max-width:600px)");

  // --- VISTA MODO GUARDADO (CARD COMPACTA) ---
  if (isLocked) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...cardShellSx,
          mb: 2,
        }}
      >
        <Box sx={cardHeaderSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.35 }}>
                <Chip
                  size="small"
                  icon={<CheckCircle sx={{ fontSize: "14px !important" }} />}
                  label="Saved"
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    bgcolor: alphaVar(colors.success?.[100] ?? "var(--fx-colors-success-100, #D1FAE5)", 0.9),
                    color: colors.success?.[700] ?? "#15803d",
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
                {row.measure ? (
                  <Chip
                    size="small"
                    label={getMeasureLabel(row.measure)}
                    sx={{
                      height: 24,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      bgcolor: colors.gray[100],
                      color: colors.gray[700],
                    }}
                  />
                ) : null}
                {frequencyTypeValue ? (
                  <Chip
                    size="small"
                    label={getJobFrequencyLabel(frequencyTypeValue)}
                    sx={{
                      height: 24,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      bgcolor: colors.gray[100],
                      color: colors.gray[700],
                    }}
                  />
                ) : null}
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color={colors.gray[800]} lineHeight={1.2} noWrap>
                {row.useServiceNameOverride && row.serviceNameOverride?.trim() ? row.serviceNameOverride.trim() : row.service || "Unnamed Service"}
              </Typography>
              {row.useServiceNameOverride && row.serviceNameOverride?.trim() ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.gray[500],
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  Original service: {row.service}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <IconButton
              onClick={() => onEnterEditMode(index)}
              size="small"
              sx={{ color: colors.gray[600], "&:hover": { color: colors.orange.base, bgcolor: colors.gray[100] } }}
            >
              <Edit sx={{ fontSize: 18 }} />
            </IconButton>
            {canDelete && onDeleteLocalAndRedux ? (
              <IconButton
                onClick={() => {
                  onRemoveFromRedux(row.id);
                  onDeleteLocalAndRedux(index);
                }}
                size="small"
                sx={{
                  color: colors.danger[500],
                  "&:hover": { bgcolor: colors.gray[100] },
                }}
              >
                <DeleteOutline sx={{ fontSize: 18 }} />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
            gap: 2,
            alignItems: { sm: "center" },
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: colors.gray[500], fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Internal notes
            </Typography>
            <Typography variant="body2" sx={{ color: colors.gray[700], mt: 0.5, fontStyle: row.notes ? "normal" : "italic" }}>
              {row.notes || "No notes added"}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: { sm: 148 },
              textAlign: { xs: "left", sm: "right" },
            }}
          >
            <Typography variant="caption" sx={{ color: colors.gray[500], fontWeight: 700, letterSpacing: 0.5 }}>
              LINE TOTAL
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: colors.gray[900], lineHeight: 1.1 }}>
              {formatMoney(Number(row.total) || 0)}
            </Typography>
            {Number(row.price) > 0 && Number(row.units) > 0 ? (
              <Typography variant="caption" sx={{ color: colors.gray[500], display: "block", mt: 0.35 }}>
                {formatMoney(Number(row.price))} × {row.units}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Paper>
    );
  }

  // --- VISTA MODO EDICIÓN (FORMULARIO) ---
  const unitPrice = Number(row.price) || 0;
  const units = Number(row.units) || 0;
  const measureLabel = getMeasureLabel(row.measure);
  const showTotalBreakdown = unitPrice > 0 && units > 0;

  return (
    <Paper elevation={0} sx={{ ...cardShellSx, mb: 2 }}>
      <Box sx={cardHeaderSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: colors.gray[500], fontWeight: 800, letterSpacing: 1.1, lineHeight: 1.2, display: "block" }}>
              Service Details #{index + 1}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[500] }}>
              Define the service, pricing, and internal notes.
            </Typography>
          </Box>
        </Box>

        {canDelete && (
          <IconButton
            onClick={() => {
              onRemoveFromRedux(row.id);
              onDeleteLocalAndRedux?.(index);
            }}
            size="small"
            sx={{ color: colors.gray[400], "&:hover": { color: colors.danger[500], bgcolor: colors.danger[50] } }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 2,
            mb: 2.5,
          }}
        >
          <FormControl fullWidth sx={baseInputStyles} disabled={isLoading} error={wasSubmitted && !row.service}>
            <InputLabel>Service Type</InputLabel>
            <Select
              label="Service Type"
              value={row.service || ""}
              MenuProps={menuPropsModal}
              IconComponent={KeyboardArrowDown}
              onChange={(e) => {
                onUpdateField(index, "service", e.target.value);
                onUpdateField(index, "serviceUid", services.find((s) => s.name === e.target.value)?.uid);
              }}
            >
              {services.map((srv) => (
                <MenuItem key={srv.name} value={srv.name}>
                  {srv.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={baseInputStyles} error={wasSubmitted && !row.measure}>
            <InputLabel>Unit of Measure</InputLabel>
            <Select
              label="Unit of Measure"
              value={row.measure || ""}
              MenuProps={menuPropsModal}
              IconComponent={KeyboardArrowDown}
              onChange={(e) => onUpdateField(index, "measure", e.target.value)}
            >
              {groupOptions(MEASURE_OPTIONS).flatMap((option) => [
                option.group ? (
                  <ListSubheader key={option.group} sx={{ fontWeight: 700, color: colors.gray[400] }}>
                    {option.group}
                  </ListSubheader>
                ) : null,
                ...option.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={baseInputStyles} error={wasSubmitted && !frequencyTypeValue}>
            <InputLabel>Frequency</InputLabel>
            <Select
              label="Frequency"
              value={frequencyTypeValue}
              MenuProps={menuPropsModal}
              IconComponent={KeyboardArrowDown}
              onChange={(e) => onUpdateField(index, "frequencyType", e.target.value)}
            >
              {JOB_FREQUENCY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <ServiceAliasField
            enabled={Boolean(row.useServiceNameOverride)}
            value={row.serviceNameOverride || ""}
            originalServiceName={row.service || ""}
            onEnabledChange={(enabled) => {
              onUpdateField(index, "useServiceNameOverride", enabled);
            }}
            onValueChange={(value) => {
              onUpdateField(index, "serviceNameOverride", value);
            }}
            error={wasSubmitted && Boolean(row.useServiceNameOverride) && !row.serviceNameOverride?.trim()}
            helperText={
              wasSubmitted && Boolean(row.useServiceNameOverride) && !row.serviceNameOverride?.trim() ? "Enter a custom service alias." : undefined
            }
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.35fr 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: { md: 196 } }}>
            {isXs ? (
              <>
                <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.gray[700] }}>
                    Internal Notes
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenNotes(!openNotes)}
                    sx={{ color: colors.gray[600], "&:hover": { bgcolor: colors.gray[100] } }}
                  >
                    {openNotes ? (
                      <ExpandLess sx={{ fontSize: 20, color: colors.orange.base }} />
                    ) : (
                      <Add sx={{ fontSize: 20, color: colors.orange.base }} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={openNotes}>
                  <TextField
                    label="Internal Notes"
                    placeholder="Add specific instructions..."
                    multiline
                    minRows={4}
                    fullWidth
                    value={row.notes || ""}
                    onChange={(e) => onUpdateField(index, "notes", e.target.value)}
                    sx={{ ...multilineStyles, flex: 1 }}
                  />
                </Collapse>
              </>
            ) : (
              <TextField
                label="Internal Notes"
                placeholder="Add specific instructions for this service..."
                multiline
                minRows={5}
                fullWidth
                value={row.notes || ""}
                onChange={(e) => onUpdateField(index, "notes", e.target.value)}
                sx={{ ...multilineStyles, flex: 1, "& .MuiOutlinedInput-root": { height: "100%", minHeight: 196 } }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 800, color: colors.gray[500], letterSpacing: 0.6, textTransform: "uppercase" }}>
              Pricing
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <TextField
                label="Unit Price"
                type="number"
                fullWidth
                error={wasSubmitted && Number(row.price) <= 0}
                value={row.price}
                onChange={(e) => onUpdateField(index, "price", e.target.value)}
                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontSize: 14, color: colors.gray[500], fontWeight: 600 }}>$</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                error={wasSubmitted && Number(row.units) <= 0}
                value={row.units}
                onChange={(e) => onUpdateField(index, "units", e.target.value)}
                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                sx={textFieldStyles}
              />
            </Box>

            <Divider sx={{ borderColor: colors.gray[100], my: 1 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: colors.gray[500], fontWeight: 700, letterSpacing: 0.4 }}>
                  Line Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, color: colors.gray[900], lineHeight: 1.1, mt: 0.25 }}>
                  {formatMoney(Number(row.total) || 0)}
                </Typography>
                {showTotalBreakdown ? (
                  <Typography variant="body2" sx={{ color: colors.gray[400], mt: 0.5, fontSize: "0.8125rem" }}>
                    {formatMoney(unitPrice)} × {units} {measureLabel}
                  </Typography>
                ) : null}
              </Box>

              <Button
                onClick={handleSave}
                disableElevation
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  py: 1,
                  minWidth: { sm: 150 },
                  bgcolor: colors.orange.base,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  color: "white",
                  alignSelf: { xs: "stretch", sm: "center" },
                  "&:hover": { bgcolor: colors.orange[600] },
                }}
              >
                Save Service
              </Button>
            </Box>

            <Box sx={{ mt: 1.5 }}>
              {wasSubmitted && !isFormValid ? (
                <Typography variant="caption" color="error" sx={{ fontWeight: 600, display: "block" }}>
                  Complete all required fields before saving.
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: colors.gray[400], fontWeight: 500, display: "block" }}>
                  Save to lock this service and continue.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
